import { useState, useEffect } from 'react';

interface RankingData {
  topReaders: {
    position: number;
    streak: number;
  }[];
  userStreak: number;
}

interface PointLeadersGraphProps {
  email: string;
}

const backendUrl = 'https://temp.testeswaffle.org';

export function PointLeadersGraph({ email }: PointLeadersGraphProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RankingData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${backendUrl}/streaks/ranking?email=${email}`);
        if (!response.ok) {
          throw new Error('Falha ao carregar o ranking');
        }
        const result = await response.json();
        setData(result.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [email]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
        <p className="text-gray-600 dark:text-gray-400">
          Não foi possível carregar os dados neste momento.
        </p>
      </div>
    );
  }

  // Ordem para exibição: 2º, 1º, 3º
  const podiumOrder = [1, 0, 2]; 
  
  // Configuração das barras com alturas menores (mais achatadas)
  const bars = data.topReaders
    .slice(0, 3)
    .map((reader, index) => ({
      editions: reader.streak,
      label: `${reader.streak} edições`,
      position: reader.position,
      height: index === 0 ? 80 : index === 1 ? 70 : 60 // Alturas reduzidas e mais achatadas
    }));

  // Verificar se o usuário está no pódio
  const isUserInPodium = data.topReaders.slice(0, 3).some(reader => reader.streak === data.userStreak);

  return (
    <div className="w-full mx-auto mt-28">
      <div className="flex items-end justify-center gap-6 h-[220px]">
        {podiumOrder.map((position) => {
          const bar = bars[position];
          const isFirst = position === 0;
          const isSecond = position === 1;
          const isUserPosition = bar.editions === data.userStreak;
          
          return (
            <div
              key={position}
              className="relative flex flex-col items-center"
              style={{ 
                height: '100%',
                transform: `translateY(${isFirst ? '-10%' : isSecond ? '0%' : '10%'})`
              }}
            >
              <div
                className="w-24 rounded-t-lg bg-[#ffd000] relative transition-all duration-300 ease-out transform hover:scale-105"
                style={{ 
                  height: `${bar.height}%`,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
                  <div className="text-3xl mb-1 text-gray-900 dark:text-white">
                    {isFirst ? '👑' : isSecond ? '🥈' : '🥉'}
                  </div>
                  <span className="font-bold text-lg text-gray-900 dark:text-white">
                    {bar.label}
                  </span>
                </div>
                
                {isUserPosition && (
                  <div className="absolute -top-20 right-0 transform translate-x-1/2">
                    <div className="text-sm bg-yellow-200 dark:bg-yellow-900 dark:text-white px-2 py-1 rounded-full">
                      você está aqui
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Só mostrar a mensagem do café se o usuário não estiver no pódio */}
      {!isUserInPodium && (
        <div className="text-center">
          <p className="text-xl font-medium text-gray-900 dark:text-white">
            {data.userStreak} edições... você está aqui.
          </p>
          <p className="text-gray-600 dark:text-gray-400 italic mt-2 mb-8">
            e o seu café tá esfriando, rs.
          </p>
        </div>
      )}
    </div>
  );
}